import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../components/ThemeModeContext';
import axios from 'axios';
import { serverDomain } from './config';

const BLANK_DOC = '<!doctype html><html><head><meta charset="utf-8"/></head><body></body></html>';
const NO_CONTENT_DOC = '<!doctype html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/><style>html,body{height:100%;margin:0}body{min-height:100vh;display:flex;align-items:center;justify-content:center;background:#111;color:#fff;font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;font-size:24px;text-align:center;padding:16px;box-sizing:border-box;}</style></head><body><h1>This app has<br>no content yet.</h1></body></html>';

export default function Run() {
  const { isDarkMode } = useThemeMode();
  const navigate = useNavigate();

  const [htmlSource, setHtmlSource] = useState('');
  const [iframeKey, setIframeKey] = useState(0);
  const [showBack, setShowBack] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  // Detect mobile via UA, with fallback for small touch-only viewports
  useEffect(() => {
    const checkMobile = () => {
      try {
        const ua = navigator.userAgent || '';
        const uaMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
        const smallTouch = (navigator.maxTouchPoints || 0) > 0 && (window.innerWidth || 0) <= 480;
        setIsMobile(uaMobile || smallTouch);
      } catch (_) {
        setIsMobile(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const styles = useMemo(() => ({
    container: { height: '100vh', width: '100vw', background: isDarkMode ? '#000' : '#fff', position: 'relative', overflow: 'hidden' },
    iframe: { position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' },
    backLink: { position: 'absolute', top: 0, left: 0, zIndex: 25, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, background: 'transparent', textDecoration: 'none', cursor: 'pointer' },
    arrowSvg: { display: 'block' },
    arrowPrimary: '#ffffff',
    arrowOutline: 'rgba(128,128,128,0.85)'
  }), [isDarkMode]);

  // Determine back link target based on JWT presence
  const backHref = useMemo(() => {
    const jwt = localStorage.getItem('appstar_jwt');
    return jwt ? '/' : '/start';
  }, []);

  const hasHtmlSource = useMemo(() => (typeof htmlSource === 'string' && htmlSource.trim().length > 0), [htmlSource]);

  useEffect(() => {
    // Determine app_id: localStorage or URL /run/:id
    const lsId = localStorage.getItem('appstar_app_id');
    let app_id = lsId && String(lsId).trim().length > 0 ? lsId : null;
    if (!app_id) {
      try {
        const m = window.location.pathname.match(/\/run\/(\d+)/);
        if (m && m[1]) app_id = m[1];
      } catch (_) {}
    }
    if (!app_id) {
      setIsFetching(false);
      navigate('/start');
      return;
    }

    const jwt = localStorage.getItem('appstar_jwt');
    const url = serverDomain + 'get-sources.php';
    setIsFetching(true);
    axios.get(url, { params: { app_id, token: jwt } })
      .then((res) => {
        let html = typeof res?.data?.data?.htmlSource === 'string' ? res.data.data.htmlSource : '';
        // Preserve escaped sequences (e.g. /[\r\n]/) as-is; only normalize real CRLF pairs.
        html = String(html).replace(/\r\n/g, '\n');

        //console.log('Final htmlSource for iframe:', { html });
        //console.log('decodeMaybeJson (html):', decodeMaybeJSON(html));
        
        //decode json
        //html = decodeMaybeJSON(html);

        //decode double escaped quotes...
        //////html = html.replace(/\\"/g, '"');

        //////html = html.replace(/\\n/g, '\n').replace(/\\r/g, '\r');

        console.log('Run htmlSource loaded:', html);



        setHtmlSource(html || '');
        setIframeKey(k => k + 1);
      })
      .catch((err) => {
        console.error('Run: get-sources error', err);
        setHtmlSource('');
        setIframeKey(k => k + 1);
      })
      .finally(() => {
        setIsFetching(false);
      });
  }, [navigate]);

function decodeMaybeJSON(value) {
        if (typeof value !== 'string') return value;

        const trimmed = value.trim();

        // Looks like JSON string or object/array
        if (
            (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
            (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
            (trimmed.startsWith('[') && trimmed.endsWith(']'))
        ) {
            try {
            return JSON.parse(trimmed);
            } catch {
            return value; // Not valid JSON
            }
        }

        return value;
    }  

  // Use an overlay hover zone to avoid iframe swallowing mouse events
  // Arrow visibility toggled by onMouseEnter/onMouseLeave of the overlay

  return (
    <div style={styles.container}>
      {!isMobile && (
        <a
          href={backHref}
          style={styles.backLink}
          aria-label="Back"
          onMouseEnter={() => setShowBack(true)}
          onMouseLeave={() => setShowBack(false)}
        >
          {showBack && (            <svg width="30" height="30" viewBox="0 0 24 24" style={styles.arrowSvg}>
                  <path d="M15 18l-6-6 6-6" fill="none" stroke={styles.arrowOutline} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M15 18l-6-6 6-6" fill="none" stroke={styles.arrowPrimary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </a>
      )}
      <iframe
        key={iframeKey}
        srcDoc={
          isFetching
            ? BLANK_DOC
            : (hasHtmlSource
                ? htmlSource
                : NO_CONTENT_DOC
              )
        }
        title="App Run"
        style={styles.iframe}
        sandbox="allow-scripts allow-modals allow-same-origin"
      />
    </div>
  );
}
